// IntentBar — 32px sentence-mode summary of the active query.
//
// REPLACES the dead "DEADLINE D · ROWS · HITS" strip on Horizon.
//
// Layout: [INTENT] <english query with chips> ………… [count] · [AI pivot]
//
// Two roles:
//   1. Read-mode: shows the query as a human sentence, chip values inline,
//      removable.
//   2. Edit-mode: focused via `/`. The whole bar becomes a single text input;
//      the host wires NL → AST compile on Enter and re-renders read-mode.

import type { filters as f } from '@eidollon/contracts';
import { FilterChip } from '@eidollon/ui-filters';
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

export interface IntentBarProps {
  schema: f.FilterSchema;
  state: f.FilterState;
  onChange: (next: f.FilterState) => void;
  /** Result count for the right side. `null` while loading. */
  count: number | null;
  /** Total in corpus, when available. */
  total?: number | null;
  /** AI pivot click target — opens AI Rail. `null` to hide. */
  aiBadge?: { count: number; tone?: 'amber' | 'primary'; onClick: () => void } | null;
  /** Whether the bar is in NL edit mode. The host owns the input value. */
  editing?: boolean;
  editValue?: string;
  onEditChange?: (next: string) => void;
  /** Submit NL query — host compiles to AST. */
  onSubmitNl?: (text: string) => void;
  /** Activate edit mode (typically called when user presses `/`). */
  onActivateEdit?: () => void;
  /** Deactivate edit mode without submitting. */
  onCancelEdit?: () => void;

  /** Free-text leftover from the most recent /v1/ai/nl-rewrite call —
   *  the slice the model couldn't map to a structured filter. Renders as a
   *  distinct "ignored" chip with two affordances: promote to FTS `q`, or
   *  drop. Solves the black-box trust problem (the user always sees what
   *  the AI didn't understand). Pass null/empty to hide. */
  leftoverQ?: string | null;
  /** Promote leftoverQ to the FTS `q` URL param (host wiring). */
  onPromoteLeftover?: (text: string) => void;
  /** Drop leftoverQ silently. */
  onDropLeftover?: () => void;

  /** Slot rendered immediately to the left of ROWS / AI badge. Hosts use this
   *  to mount the platform `SavedView` primitive on every entity browser
   *  (Iron Rule #11). Intel keeps SavedView in IntelViewTabs.rightSlot
   *  because that surface owns its own tab strip; everywhere else, the
   *  IntentBar is the single canonical mount point. */
  rightSlot?: ReactNode;
  /** Localized chrome labels. Host wires t() — keeps i18n out of the platform. */
  labels?: {
    intent?: string;
    placeholder?: string;
    pressSlash?: string;
    rows?: string;
    of?: string;
    aiBadge?: (count: number) => string;
    aiTitle?: string;
    ignored?: string;
    nlPlaceholder?: string;
  };
  /** Host-provided per-field label translator (for inline chip labels). */
  formatFieldLabel?: (fieldId: string, fallback: string) => string;
  /** Host-provided per-enum-value translator (for inline chip values). */
  formatOptionLabel?: (fieldId: string, value: string) => string | undefined;
}

export function IntentBar({
  schema,
  state,
  onChange,
  count,
  total,
  aiBadge,
  editing = false,
  editValue = '',
  onEditChange,
  onSubmitNl,
  onActivateEdit,
  onCancelEdit,
  leftoverQ,
  onPromoteLeftover,
  onDropLeftover,
  rightSlot,
  labels,
  formatFieldLabel,
  formatOptionLabel,
}: IntentBarProps) {
  const L = {
    intent: labels?.intent ?? 'INTENT',
    placeholder: labels?.placeholder ?? 'any opportunity · press / to refine',
    pressSlash: labels?.pressSlash ?? 'press / to edit',
    rows: labels?.rows ?? 'ROWS',
    of: labels?.of ?? 'of',
    aiBadge: labels?.aiBadge ?? ((n: number) => `AI: ${n} for you ▸`),
    aiTitle: labels?.aiTitle ?? 'open AI suggestions',
    ignored: labels?.ignored ?? 'IGNORED',
    nlPlaceholder:
      labels?.nlPlaceholder ?? 'try "Air Force C2 work over $50M closing in 60 days, not Leidos"',
  };
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const fields = new Map(schema.fields.map((field) => [field.id, field]));
  const activeIds = Object.keys(state).filter((id) => isMeaningful(state[id]));
  const remove = (id: string) => {
    const next = { ...state };
    delete next[id];
    onChange(next);
  };

  return (
    <div
      data-component="IntentBar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        height: 32,
        padding: '0 12px',
        borderBottom: '1px solid var(--bd)',
        background: 'var(--bg-pane)',
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          color: 'var(--fg-3)',
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        {L.intent}
      </span>

      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => onEditChange?.(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSubmitNl?.(editValue);
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              onCancelEdit?.();
            }
          }}
          placeholder={L.nlPlaceholder}
          style={{
            flex: 1,
            height: 24,
            padding: '0 8px',
            background: 'var(--bg-base)',
            border: '1px solid var(--bd-strong)',
            color: 'var(--fg-1)',
            fontFamily: 'var(--mono)',
            fontSize: 12,
          }}
        />
      ) : (
        <div
          onClick={onActivateEdit}
          title={L.pressSlash}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            overflow: 'hidden',
            cursor: 'text',
            color: 'var(--fg-1)',
            fontFamily: 'var(--mono)',
            fontSize: 12,
          }}
        >
          {activeIds.length === 0 ? (
            <span style={{ color: 'var(--fg-3)' }}>{L.placeholder}</span>
          ) : (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {activeIds.map((id, i) => {
                const field = fields.get(id);
                if (!field) return null;
                return (
                  <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {i > 0 && <span style={{ color: 'var(--fg-3)' }}>·</span>}
                    <FilterChip
                      label={formatFieldLabel ? formatFieldLabel(id, field.label) : field.label}
                      value={renderValue(state[id], (v) => formatOptionLabel?.(id, v))}
                      onRemove={() => remove(id)}
                    />
                  </span>
                );
              })}
            </span>
          )}
        </div>
      )}

      {/* Leftover-q "ignored" chip. Renders only when the most recent NL
          rewrite produced free-text the model couldn't map. Two affordances:
          promote to FTS `q` ("search as text"), or drop. The amber border
          telegraphs "we didn't fully understand this" — the user is never
          left wondering what the AI did with their input. */}
      {leftoverQ && leftoverQ.trim().length > 0 && (
        <span
          role="group"
          aria-label="natural-language leftover not mapped to a filter"
          title={`The AI couldn't map "${leftoverQ}" to a filter. Search as text, or drop.`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 22,
            padding: '0 4px 0 8px',
            background: 'rgba(255,176,0,0.10)',
            border: '1px dashed var(--amber, #ffb000)',
            color: 'var(--fg-1)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
            maxWidth: 240,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              color: 'var(--fg-3)',
              fontStyle: 'normal',
              fontSize: 9,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
            }}
          >
            {L.ignored}
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {leftoverQ.length > 32 ? `${leftoverQ.slice(0, 30)}…` : leftoverQ}
          </span>
          {onPromoteLeftover && (
            <button
              type="button"
              onClick={() => onPromoteLeftover(leftoverQ)}
              title="search as text"
              aria-label="promote to free-text search"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--amber, #ffb000)',
                fontFamily: 'var(--mono)',
                fontSize: 10,
                fontStyle: 'normal',
              }}
            >
              ⌕
            </button>
          )}
          {onDropLeftover && (
            <button
              type="button"
              onClick={onDropLeftover}
              aria-label="drop leftover"
              title="drop"
              style={{
                background: 'transparent',
                border: 'none',
                padding: '0 2px',
                cursor: 'pointer',
                color: 'var(--fg-3)',
                fontSize: 12,
                fontStyle: 'normal',
              }}
            >
              ×
            </button>
          )}
        </span>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0,
          color: 'var(--fg-2)',
          fontFamily: 'var(--mono)',
          fontSize: 11,
        }}
      >
        {rightSlot}
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {count == null ? '…' : count.toLocaleString()} {L.rows}
          {total != null && total !== count && (
            <span style={{ color: 'var(--fg-3)', marginLeft: 6 }}>
              {L.of} {total.toLocaleString()}
            </span>
          )}
        </span>
        {aiBadge && aiBadge.count > 0 && (
          <button
            type="button"
            onClick={aiBadge.onClick}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: aiBadge.tone === 'primary' ? 'var(--primary)' : 'var(--amber)',
              fontFamily: 'var(--mono)',
              fontSize: 11,
            }}
            title={L.aiTitle}
          >
            {L.aiBadge(aiBadge.count)}
          </button>
        )}
      </div>
    </div>
  );
}

function isMeaningful(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'string') return v.length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'boolean') return true;
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    return Object.values(obj).some((x) => x !== null && x !== undefined && x !== '');
  }
  return false;
}

function renderValue(v: unknown, fmt?: (value: string) => string | undefined): ReactNode {
  const tx = (s: string) => fmt?.(s) ?? s;
  if (v == null) return '';
  if (typeof v === 'string') return tx(v);
  if (Array.isArray(v)) {
    const xs = v.map((x) => (typeof x === 'string' ? tx(x) : String(x)));
    return xs.length <= 3 ? xs.join(', ') : `${xs.slice(0, 2).join(', ')} +${xs.length - 2}`;
  }
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    if ('from' in obj || 'to' in obj) {
      const f = (obj.from as string | null | undefined) ?? '';
      const t = (obj.to as string | null | undefined) ?? '';
      if (f && t) return `${f} → ${t}`;
      if (f) return `≥ ${f}`;
      if (t) return `≤ ${t}`;
    }
    if ('min' in obj || 'max' in obj) {
      const mi = (obj.min as number | null | undefined) ?? null;
      const ma = (obj.max as number | null | undefined) ?? null;
      const fmt = (n: number) => n.toLocaleString();
      if (mi != null && ma != null) return `${fmt(mi)} → ${fmt(ma)}`;
      if (mi != null) return `≥ ${fmt(mi)}`;
      if (ma != null) return `≤ ${fmt(ma)}`;
    }
  }
  return JSON.stringify(v);
}
